import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Loader2, Save, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime, toISTDateStr } from '../../lib/utils';

export default function EditSession() {
  const { batchId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const scope = searchParams.get('scope') || 'this_session';
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [batch, setBatch] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [weekSessions, setWeekSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', instructor_id: '', duration_minutes: 60, scheduled_date: '', scheduled_time: '' });
  const [currentWeekStart, setCurrentWeekStart] = useState(new Date());
  const [affectedSessions, setAffectedSessions] = useState([]);
  const [applyAllTime, setApplyAllTime] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [sessRes, batchRes, instRes] = await Promise.all([
          api.get(`/api/sessions/${sessionId}`),
          api.get(`/api/batches/${batchId}`),
          api.get('/api/users', { params: { role: 'instructor', limit: 100 } }),
        ]);
        setSession(sessRes.data);
        setBatch(batchRes.data);
        // Include instructors + superadmin only (regular admins can't host)
        const instructorUsers = instRes.data.users || [];
        const adminRes = await api.get('/api/users', { params: { role: 'admin', limit: 50 } });
        const superadmins = (adminRes.data.users || []).filter(u => u.role === 'superadmin');
        const allTeachers = [...instructorUsers, ...superadmins];
        const seen = new Set();
        setInstructors(allTeachers.filter(u => { if (seen.has(u.id)) return false; seen.add(u.id); return true; }));
        setForm({
          title: sessRes.data.title || 'Live Session',
          instructor_id: sessRes.data.instructor_id || '',
          duration_minutes: sessRes.data.duration_minutes || 60,
          scheduled_date: sessRes.data.scheduled_date || '',
          scheduled_time: sessRes.data.scheduled_time || '',
        });
        // Set week to the session's date
        const d = new Date(sessRes.data.scheduled_date + 'T00:00:00');
        const day = d.getDay();
        const weekStart = new Date(d);
        weekStart.setDate(d.getDate() - day);
        setCurrentWeekStart(weekStart);
        
        // Fetch affected sessions based on scope
        if (scope !== 'this_session') {
          try {
            const allSess = await api.get('/api/sessions', { params: { batch_id: batchId, status: 'upcoming', limit: 200 } });
            const sess = sessRes.data;
            const sessDate = new Date(sess.scheduled_date + 'T00:00:00');
            const sessDow = sessDate.getDay();
            let affected = allSess.data.sessions.filter(s => {
              // Use string comparison for dates (both are YYYY-MM-DD IST strings)
              if (s.scheduled_date < sess.scheduled_date) return false;
              const sDate = new Date(s.scheduled_date + 'T00:00:00');
              if (scope === 'all_upcoming') return true;
              if (scope === 'same_day') return sDate.getDay() === sessDow;
              if (scope === 'same_day_time') return sDate.getDay() === sessDow && s.scheduled_time === sess.scheduled_time;
              if (scope === 'same_time') return s.scheduled_time === sess.scheduled_time;
              if (scope === 'same_instructor') return s.instructor_id === sess.instructor_id;
              return false;
            });
            setAffectedSessions(affected.sort((a,b) => (a.scheduled_date + a.scheduled_time).localeCompare(b.scheduled_date + b.scheduled_time)));
            setApplyAllTime(sess.scheduled_time || '');
          } catch {}
        }
      } catch (err) {
        toast.error('Failed to load session');
      }
      setLoading(false);
    };
    load();
  }, [sessionId, batchId]);

  // Load calendar sessions for the current week
  useEffect(() => {
    const start = new Date(currentWeekStart);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    api.get('/api/calendar', {
      params: { date_from: toISTDateStr(start), date_to: toISTDateStr(end) }
    }).then(res => setWeekSessions(res.data.sessions));
  }, [currentWeekStart]);

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(currentWeekStart);
    d.setDate(currentWeekStart.getDate() + i);
    return d;
  });

  const hours = Array.from({ length: 16 }, (_, i) => i + 5); // 5 AM to 8 PM

  const handleUpdate = async () => {
    setSaving(true);
    try {
      if (scope === 'this_session') {
        await api.put(`/api/sessions/${sessionId}`, form);
      } else {
        // First apply bulk edit for shared fields (title, instructor, duration)
        const res = await api.post(`/api/sessions/${sessionId}/bulk-edit`, { scope, updates: form });
        
        // Then apply individual date/time changes for sessions that were edited
        const modifiedSessions = affectedSessions.filter(s => s._modified);
        for (const s of modifiedSessions) {
          try {
            await api.put(`/api/sessions/${s.id}`, {
              scheduled_date: s.scheduled_date,
              scheduled_time: s.scheduled_time,
            });
          } catch (err) {
            toast.error(`Failed to update ${s.scheduled_date} ${s.scheduled_time}: ${err.response?.data?.detail || 'Error'}`);
          }
        }
        
        // Show conflict warning if any
        if (res.data?.warning && res.data?.conflicts?.length > 0) {
          toast.error(`${res.data.conflicts.length} scheduling conflict(s) detected! Check the calendar.`, { duration: 5000 });
        }
      }
      toast.success(scope === 'this_session' ? 'Session updated' : `Updated sessions (${scope})`);
      navigate(`/admin/batches/${batchId}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || 'Failed to update');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/batches/${batchId}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground">{batch?.name} &gt; Edit Session</p>
          <h1 className="font-heading text-2xl font-semibold">Edit Session</h1>
        </div>
        <Button onClick={handleUpdate} disabled={saving} data-testid="update-session-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Update
        </Button>
      </div>

      {scope !== 'this_session' && (
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
          <p className="text-sm text-amber-800">Editing: <strong>{scope.replace(/_/g, ' ')}</strong>. Changes will apply to multiple sessions.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Edit Form */}
        <div>
          <Card className="rounded-2xl sticky top-4">
            <CardContent className="pt-5 space-y-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Session name</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="edit-session-name" />
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Instructor</label>
                <Select value={form.instructor_id} onValueChange={v => setForm({...form, instructor_id: v})}>
                  <SelectTrigger data-testid="edit-instructor-select"><SelectValue placeholder="Select instructor" /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4} className="z-[200] max-h-60">
                    {instructors.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium mb-1.5 block">Duration</label>
                <Select value={String(form.duration_minutes)} onValueChange={v => setForm({...form, duration_minutes: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 hour</SelectItem>
                    <SelectItem value="90">1hr 30min</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {scope === 'this_session' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Date</label>
                    <Input type="date" value={form.scheduled_date} onChange={e => setForm({...form, scheduled_date: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1.5 block">Time</label>
                    <Input type="time" value={form.scheduled_time} onChange={e => setForm({...form, scheduled_time: e.target.value})} />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Affected Sessions List — Editable like Wise.live */}
          {affectedSessions.length > 0 && (
            <Card className="rounded-2xl mt-4">
              <CardContent className="pt-4">
                <h4 className="text-sm font-medium mb-2">Sessions ({affectedSessions.length})</h4>
                <p className="text-xs text-muted-foreground mb-3">Set one time to apply to every session below, or edit any row individually.</p>
                <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg border border-primary/30 bg-primary/5" data-testid="apply-time-all-row">
                  <span className="text-xs font-medium text-primary whitespace-nowrap">Set time for all</span>
                  <Input
                    type="time"
                    className="h-8 text-xs w-28"
                    value={applyAllTime}
                    onChange={e => setApplyAllTime(e.target.value)}
                    data-testid="apply-time-all-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={!applyAllTime}
                    onClick={() => {
                      setAffectedSessions(prev => prev.map(s => ({ ...s, scheduled_time: applyAllTime, _modified: true })));
                      toast.success(`Applied ${formatTime(applyAllTime)} to all ${affectedSessions.length} sessions`);
                    }}
                    data-testid="apply-time-all-button"
                  >
                    Apply to all
                  </Button>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin">
                  {affectedSessions.map((s, i) => {
                    const d = new Date(s.scheduled_date + 'T00:00:00');
                    return (
                      <div key={s.id || i} className="flex items-center gap-2 p-2.5 rounded-lg border bg-muted/20 text-sm">
                        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                        <Input
                          type="date"
                          className="h-8 text-xs flex-1"
                          value={s.scheduled_date}
                          onChange={e => {
                            const updated = [...affectedSessions];
                            updated[i] = { ...updated[i], scheduled_date: e.target.value, _modified: true };
                            setAffectedSessions(updated);
                          }}
                        />
                        <Input
                          type="time"
                          className="h-8 text-xs w-28"
                          value={s.scheduled_time}
                          onChange={e => {
                            const updated = [...affectedSessions];
                            updated[i] = { ...updated[i], scheduled_time: e.target.value, _modified: true };
                            setAffectedSessions(updated);
                          }}
                        />
                        {s._modified && <span className="text-[10px] text-amber-600 font-medium">edited</span>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right: Week Calendar */}
        <div className="lg:col-span-2">
          <Card className="rounded-2xl overflow-hidden">
            <CardContent className="p-0">
              <div className="flex items-center justify-between p-3 border-b">
                <Button variant="ghost" size="icon" onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(d); }}>&lt;</Button>
                <span className="text-sm font-medium">
                  {weekDays[0]?.toLocaleDateString('en', { month: 'short', day: 'numeric' })} - {weekDays[6]?.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <Button variant="ghost" size="icon" onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(d); }}>&gt;</Button>
              </div>
              <div className="grid grid-cols-8 border-b">
                <div className="p-2 border-r" />
                {weekDays.map((d, i) => {
                  const isToday = d.toDateString() === new Date().toDateString();
                  return (
                    <div key={i} className={`p-2 text-center border-r last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}>
                      <p className="text-xs text-muted-foreground">{d.toLocaleDateString('en', { weekday: 'short' })}</p>
                      <p className={`text-base font-semibold ${isToday ? 'bg-primary text-white rounded-full w-7 h-7 flex items-center justify-center mx-auto' : ''}`}>{d.getDate()}</p>
                    </div>
                  );
                })}
              </div>
              <div className="max-h-[500px] overflow-y-auto scrollbar-thin">
                {hours.map(hour => (
                  <div key={hour} className="grid grid-cols-8 border-b min-h-[48px]">
                    <div className="p-1 text-[11px] text-muted-foreground border-r flex items-start justify-end pr-2 pt-1">
                      {hour > 12 ? `${hour - 12}:00 pm` : hour === 12 ? '12:00 pm' : `${hour}:00 am`}
                    </div>
                    {weekDays.map((d, i) => {
                      const dateStr = toISTDateStr(d);
                      const daySessions = weekSessions.filter(s => {
                        if (s.scheduled_date !== dateStr) return false;
                        const [h] = (s.scheduled_time || '').split(':').map(Number);
                        return h === hour;
                      });
                      return (
                        <div key={i} className="border-r last:border-r-0 p-0.5 relative">
                          {daySessions.map(s => (
                            <div key={s.id}
                              className={`rounded-lg border px-1.5 py-1 text-[11px] mb-0.5 ${
                                s.id === sessionId
                                  ? 'bg-primary/20 border-primary ring-1 ring-primary'
                                  : s.batch_type === 'one_on_one' ? 'bg-teal-50 border-teal-200' : 'bg-blue-50 border-blue-200'
                              }`}>
                              <p className="font-medium truncate">{s.batch_name || s.title}</p>
                              <p className="text-muted-foreground">{formatTime(s.scheduled_time)}</p>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
